import { Icon, IconProps, P } from '@fashionnovateam/nova-ui-toolkit';
import { DateTime } from 'luxon';
import { RequestedForItem } from './types';

type RequestedForConfig = {
  icon: IconProps['name'] | ((item: RequestedForItem) => IconProps['name']);
  bgColor: string | ((item: RequestedForItem) => string);
  label: string;
  formatDate: (item: RequestedForItem) => string;
};

const isFallSeason = (season?: string): boolean =>
  season?.toLowerCase().startsWith('fall') ?? false;

const CONFIG: Record<RequestedForItem['type'], RequestedForConfig> = {
  preseason: {
    icon: (item) => (isFallSeason(item.season) ? 'cloud/cloud-sun' : 'sun'),
    bgColor: (item) =>
      isFallSeason(item.season) ? 'bg-warning-900' : 'bg-warning-700',
    label: 'Preseason',
    formatDate: (item) => item.season || '',
  },
  weekly: {
    icon: 'calendar/calendar-event',
    bgColor: 'bg-primary-700',
    label: 'Weekly',
    formatDate: (item) =>
      item.date ? DateTime.fromISO(item.date).toFormat('MMM dd, yyyy') : '',
  },
  monthly: {
    icon: 'calendar/calendar-filled',
    bgColor: 'bg-secondary-700',
    label: 'Monthly',
    formatDate: (item) =>
      item.date ? DateTime.fromISO(item.date).toFormat('MMM yyyy') : '',
  },
};

type RequestedForCellProps = {
  items: RequestedForItem[] | undefined;
};

export function RequestedForCell({ items }: RequestedForCellProps) {
  if (!items || items.length === 0) {
    return (
      <div className="flex items-center w-full h-full pl-3">
        <P size="2xs" color="disabled">
          -
        </P>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center gap-1 w-full h-full pl-3">
      {items.map((item, index) => {
        const config = CONFIG[item.type];
        if (!config) return null;

        const key = `${item.type}-${item.date || item.season || ''}-${index}`;
        const dateStr = config.formatDate(item);

        const iconName =
          typeof config.icon === 'function' ? config.icon(item) : config.icon;
        const bgColorClass =
          typeof config.bgColor === 'function'
            ? config.bgColor(item)
            : config.bgColor;

        return (
          <div key={key} className="flex items-center gap-2">
            <div
              className={`w-4 h-4 rounded-[5px] flex items-center justify-center shrink-0 ${bgColorClass}`}
            >
              <Icon name={iconName} size="10px" className="text-white" />
            </div>
            <div className="flex items-center gap-1">
              <P size="2xs" color="disabled" className="font-normal">
                {config.label}
              </P>
              {dateStr && (
                <P size="2xs" color="primary" className="font-normal">
                  {dateStr}
                </P>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
